import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Copy,
  ClipboardPaste,
  RotateCcw,
  Share2,
  Play,
  Search,
  Filter,
  Heart,
  Shuffle,
  History,
  X,
  Grid,
  List,
  MoreVertical,
  Image,
  GripVertical,
  ExternalLink,
} from "lucide-react";
import { useFavorites } from "@/hooks/use-favorites";
import { useTextHistory } from "@/hooks/use-text-history";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CharacterCounter } from "@/components/CharacterCounter";
import { KeyboardShortcuts, KeyboardShortcutsButton } from "@/components/KeyboardShortcuts";
import { ImageExport } from "@/components/ImageExport";
import { SurpriseMeModal } from "@/components/SurpriseMeModal";

import { transformText } from "@/lib/transformers";
import { useToast } from "@/hooks/use-toast";
import stylesData from "@/data/styles.json";

// Default phrase for Surprise Me when input is empty
const DEFAULT_SURPRISE_TEXT = "Your fancy text here";

interface Style {
  slug: string;
  name: string;
  shortDescription: string;
  longDescription: string;
  tags: string[];
  example: string;
  unicodeMap: string;
}

// Sortable wrapper component for drag and drop
interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function SortableItem({ id, children, disabled }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

interface GeneratorProps {
  initialText?: string;
  pageSize?: number;
  onStyleSelect?: (slug: string) => void;
  currentStyleSlug?: string;
  hideStyleHeading?: boolean;
  hideFilters?: boolean;
  hideLoadMore?: boolean;
  initialSelectedTags?: string[];
  onFilteredCountChange?: (count: number) => void;
  clearRedirectUrl?: string;
}

export function Generator({
  initialText = "",
  pageSize = 24,
  onStyleSelect,
  currentStyleSlug,
  hideStyleHeading = false,
  hideFilters = false,
  hideLoadMore = false,
  initialSelectedTags = [],
  onFilteredCountChange,
  clearRedirectUrl,
}: GeneratorProps) {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState(initialText);
  const [focusedStyle, setFocusedStyle] = useState<string | null>(null);
  const [preserveAscii, setPreserveAscii] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>(initialSelectedTags);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const { favorites, toggleFavorite, isFavorite, clearFavorites, reorderFavorites, favoritesCount } = useFavorites();
  const { history, addToHistory, removeFromHistory, clearHistory, historyCount } = useTextHistory();
  const { viewMode, setViewMode, truncateLength, showDescriptions } = useViewPreferences();
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSurpriseModal, setShowSurpriseModal] = useState(false);
  const [surpriseText, setSurpriseText] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Only update selectedTags when initialSelectedTags prop changes AND it's different from current state
  // This prevents resetting user selections on the home page
  useEffect(() => {
    // Only update if we're on a style detail page (where initialSelectedTags would have meaningful values)
    if (currentStyleSlug && initialSelectedTags.length > 0) {
      setSelectedTags(initialSelectedTags);
    }
  }, [initialSelectedTags, currentStyleSlug]);

  // Reset favorites filter only when navigating to a DIFFERENT style page (not on every render)
  const prevStyleSlugRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Only reset if we're navigating from one style to another (slug actually changed)
    if (currentStyleSlug && prevStyleSlugRef.current !== undefined && prevStyleSlugRef.current !== currentStyleSlug) {
      setShowFavoritesOnly(false);
    }
    prevStyleSlugRef.current = currentStyleSlug;
  }, [currentStyleSlug]);
  const [showAllTags, setShowAllTags] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  // Define popular tags for filtering (using actual tags from styles data)
  const allPopularTags = [
    "social",
    "instagram",
    "tiktok",
    "elegant",
    "professional",
    "modern",
    "aesthetic",
    "clean",
    "minimalist",
    "bold",
    "emphasis",
    "sophisticated",
    "formal",
    "creative",
    "casual",
    "decorative",
    "fun",
    "playful",
    "unique",
    "gothic",
    "medieval",
    "retro",
    "contemporary",
    "tech",
    "quotes",
    "captions",
    "handwritten",
    "cursive",
    "signature",
    "academic",
    "mathematical",
  ];

  // Calculate optimal number of tags for exactly 2 rows based on screen width
  const [tagsPerRow, setTagsPerRow] = useState(8);

  useEffect(() => {
    const calculateTagsPerRow = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1280) return 9; // xl screens
      if (screenWidth >= 1024) return 8; // lg screens
      if (screenWidth >= 768) return 7; // md screens
      if (screenWidth >= 640) return 6; // sm screens
      return 4; // mobile screens
    };

    setTagsPerRow(calculateTagsPerRow());

    const handleResize = () => {
      setTagsPerRow(calculateTagsPerRow());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get tags to show (exactly 2 rows) - memoized to prevent unnecessary recalculations
  const popularTags = useMemo(() => allPopularTags.slice(0, tagsPerRow * 2), [tagsPerRow]);

  // Get all unique tags - memoized
  const allTags = useMemo(() => Array.from(new Set(stylesData.flatMap((style) => style.tags))), []);

  // Filter tags to show based on showAllTags state - memoized
  const tagsToShow = useMemo(() => {
    return showAllTags
      ? allTags
      : [...new Set([...selectedTags, ...popularTags.filter((tag) => allTags.includes(tag))])];
  }, [showAllTags, allTags, selectedTags, popularTags]);

  // Ensure all selected tags are visible, even if not in popular tags - memoized
  const visibleTags = useMemo(() => {
    return [...new Set([...selectedTags, ...tagsToShow])];
  }, [selectedTags, tagsToShow]);

  // Filter styles based on search, tags, favorites, and current style
  const { allFilteredStyles, totalFilteredCount } = useMemo(() => {
    // When showing favorites only, filter to show ONLY favorites
    if (showFavoritesOnly) {
      // If no favorites, return empty array
      if (favorites.length === 0) {
        return {
          allFilteredStyles: [],
          totalFilteredCount: 0,
        };
      }

      let favoriteStyles = favorites
        .map((slug) => stylesData.find((s) => s.slug === slug))
        .filter((s): s is Style => s !== undefined);

      // Apply search filter to favorites if there's a search term
      if (searchTerm.trim() !== "") {
        favoriteStyles = favoriteStyles.filter(
          (style) =>
            style.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            style.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
            style.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
        );
      }

      return {
        allFilteredStyles: favoriteStyles,
        totalFilteredCount: favoriteStyles.length,
      };
    }

    let filtered = stylesData.filter((style) => {
      const matchesSearch =
        searchTerm === "" ||
        style.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        style.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        style.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesTags = selectedTags.length === 0 || selectedTags.some((tag) => style.tags.includes(tag));

      // If there's a search term, prioritize search results over tag filtering
      if (searchTerm.trim() !== "") {
        return matchesSearch;
      }

      // If no search term, apply both search and tag filters
      return matchesSearch && matchesTags;
    });

    const totalCount = filtered.length;

    // Handle current style prioritization - this takes precedence on style detail pages
    if (currentStyleSlug) {
      const currentStyle = filtered.find((s) => s.slug === currentStyleSlug);
      const otherStyles = filtered.filter((s) => s.slug !== currentStyleSlug);
      filtered = currentStyle ? [currentStyle, ...otherStyles] : filtered;
    } else {
      // Only sort favorites to the top on the home page (not style detail pages)
      // This ensures the current style always appears first on /style/* pages
      if (favorites.length > 0) {
        const favoriteStyles = filtered.filter((s) => favorites.includes(s.slug));
        const nonFavoriteStyles = filtered.filter((s) => !favorites.includes(s.slug));
        filtered = [...favoriteStyles, ...nonFavoriteStyles];
      }
    }

    return {
      allFilteredStyles: filtered,
      totalFilteredCount: totalCount,
    };
  }, [showFavoritesOnly, favorites, searchTerm, selectedTags, currentStyleSlug]);

  // Apply pagination - slice to visibleCount
  const filteredStyles = useMemo(() => {
    return allFilteredStyles.slice(0, visibleCount);
  }, [allFilteredStyles, visibleCount]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [searchTerm, selectedTags, showFavoritesOnly, pageSize]);

  // Notify parent of filtered count changes
  useEffect(() => {
    onFilteredCountChange?.(totalFilteredCount);
  }, [totalFilteredCount, onFilteredCountChange]);

  const hasMoreStyles = visibleCount < totalFilteredCount;

  const loadMoreStyles = () => {
    setVisibleCount((prev) => Math.min(prev + pageSize, totalFilteredCount));
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = favorites.indexOf(active.id as string);
      const newIndex = favorites.indexOf(over.id as string);
      const newOrder = arrayMove(favorites, oldIndex, newIndex);
      reorderFavorites(newOrder);
    }
  };

  const styles = filteredStyles;

  useEffect(() => {
    // Handle URL parameter for text
    const params = new URLSearchParams(window.location.search);
    const urlText = params.get("text");
    if (urlText) {
      setInputText(decodeURIComponent(urlText));
    }
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields (except for specific ones)
      const target = e.target as HTMLElement;
      const isInputField = target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      // ? - Show keyboard shortcuts (works everywhere)
      if (e.key === "?" && !isInputField) {
        e.preventDefault();
        setShowShortcuts(true);
        return;
      }

      // Escape - Clear search & filters
      if (e.key === "Escape") {
        e.preventDefault();
        if (searchTerm || selectedTags.length > 0 || showFavoritesOnly) {
          clearAllFilters();
        }
        return;
      }

      // Ctrl/Cmd + K - Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      // Ctrl/Cmd + Shift + R - Surprise Me (only when not in input)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "R") {
        e.preventDefault();
        handleSurpriseMe();
        return;
      }

      // Ctrl/Cmd + Shift + F - Toggle favorites filter
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "F") {
        e.preventDefault();
        setShowFavoritesOnly((prev) => !prev);
        return;
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [searchTerm, selectedTags, showFavoritesOnly]);

  const processText = (text: string) => {
    if (!text.trim()) return text;
    if (preserveAscii) {
      // Strip emojis and non-ASCII characters
      return text.replace(/[^\x00-\x7F]/g, "");
    }
    return text;
  };

  const handleCopy = async (transformedText: string, styleName: string) => {
    try {
      await navigator.clipboard.writeText(transformedText);
      // Add original text to history when copying
      if (inputText.trim()) {
        addToHistory(inputText.trim());
      }
      toast({
        title: "Copied!",
        description: `${styleName} text copied to clipboard`,
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Please try selecting and copying manually",
        variant: "destructive",
      });
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputText(text);
    } catch (err) {
      toast({
        title: "Paste failed",
        description: "Please paste manually into the text field",
        variant: "destructive",
      });
    }
  };

  const handleClear = () => {
    setInputText("");
    setFocusedStyle(null);
    setSearchTerm("");
    setSelectedTags([]);
    setShowFavoritesOnly(false);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const clearAllFilters = () => {
    if (clearRedirectUrl) {
      navigate(clearRedirectUrl);
      return;
    }
    setSelectedTags([]);
    setSearchTerm("");
    setShowFavoritesOnly(false);
  };

  const handleShare = (style: Style) => {
    const url = new URL(window.location.href);
    url.searchParams.set("text", encodeURIComponent(inputText));
    url.searchParams.set("style", style.slug);

    navigator.clipboard.writeText(url.toString());
    toast({
      title: "Link copied!",
      description: `Shareable link with ${style.name} style copied`,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && focusedStyle) {
      e.preventDefault();
      const style = styles.find((s) => s.slug === focusedStyle);
      if (style) {
        const transformedText = transformText(processText(inputText), style.unicodeMap);
        handleCopy(transformedText, style.name);
      }
    }
  };

  const handleSurpriseMe = () => {
    // Use input text or default phrase
    const textToTransform = inputText.trim() || DEFAULT_SURPRISE_TEXT;
    setSurpriseText(textToTransform);
    setShowSurpriseModal(true);
  };

  const handleSurpriseCopy = (text: string, styleName: string) => {
    // Add to history if using actual user input
    if (inputText.trim()) {
      addToHistory(inputText.trim());
    }
    toast({
      title: `Surprise! ${styleName}`,
      description: `"${text.slice(0, 50)}${text.length > 50 ? "..." : ""}" copied!`,
    });
    setShowSurpriseModal(false);
  };

  const maxChars = 1000;

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Input Section */}
      <Card className="p-6 gradient-card shadow-card">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-semibold">Enter your text</h2>
            <CharacterCounter text={inputText} maxChars={maxChars} />
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value.slice(0, maxChars))}
                onKeyDown={handleKeyDown}
                placeholder="Type or paste your text..."
                className="min-w-[200px] h-10 min-h-[40px] resize-none"
                style={{ height: "40px" }}
              />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search styles... (Ctrl+K)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 min-w-[200px] h-10"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handlePaste} variant="outline" size="sm" className="gap-2">
              <ClipboardPaste size={16} />
              Paste
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" disabled={historyCount === 0}>
                  <History size={16} />
                  History {historyCount > 0 && `(${historyCount})`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Recent Texts</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.preventDefault();
                      clearHistory();
                    }}
                  >
                    Clear all
                  </Button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {history.map((item, index) => (
                  <DropdownMenuItem
                    key={index}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                    onClick={() => setInputText(item.text)}
                  >
                    <span className="truncate flex-1">{item.text}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 shrink-0 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromHistory(item.text);
                      }}
                    >
                      <X size={12} />
                    </Button>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button onClick={handleClear} variant="outline" size="sm" className="gap-2">
              <RotateCcw size={16} />
              Clear
            </Button>

            {/* Favorites Toggle - Works on all devices */}
            <Button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              variant={showFavoritesOnly ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <Heart size={16} className={showFavoritesOnly ? "fill-current" : ""} />
              Favorites {favoritesCount > 0 && `(${favoritesCount})`}
            </Button>

            <Button
              onClick={handleSurpriseMe}
              variant="outline"
              size="sm"
              className="gap-2 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 border-primary/30"
            >
              <Shuffle size={16} />
              Surprise Me!
            </Button>

            {/* View Mode Toggle */}
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                className="rounded-r-none px-2"
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <Grid size={16} />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                className="rounded-l-none px-2"
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <List size={16} />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="preserve-ascii"
                checked={preserveAscii}
                onCheckedChange={(checked) => setPreserveAscii(checked as boolean)}
              />
              <label
                htmlFor="preserve-ascii"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Preserve ASCII only
              </label>
            </div>
          </div>

          {/* Filter by use case - Only show when NOT on style detail page and not hidden */}
          {!hideFilters && !window.location.pathname.startsWith("/style/") && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium">Filter by use case:</span>
                {selectedTags.length > 0 &&
                  (clearRedirectUrl ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={clearAllFilters}
                          variant="ghost"
                          size="sm"
                          className="text-xs text-muted-foreground hover:text-foreground gap-1"
                        >
                          Clear ({selectedTags.length})
                          <ExternalLink size={12} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View all styles</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      onClick={clearAllFilters}
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear ({selectedTags.length})
                    </Button>
                  ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {visibleTags.map((tag) => (
                  <Button
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleTag(tag)}
                    className="text-xs"
                  >
                    {tag}
                  </Button>
                ))}
              </div>
              {!showAllTags && allTags.length > visibleTags.length && (
                <Button
                  onClick={() => setShowAllTags(true)}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Show all {allTags.length} tags
                </Button>
              )}
              {showAllTags && (
                <Button
                  onClick={() => setShowAllTags(false)}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Show less
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Results Info - hide on platform pages */}
      {!hideFilters && (searchTerm || selectedTags.length > 0 || showFavoritesOnly) && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-muted-foreground text-sm">
            Showing {totalFilteredCount} {showFavoritesOnly ? "favorite " : ""}styles
            {searchTerm && ` matching "${searchTerm}"`}
            {selectedTags.length > 0 && ` filtered by: ${selectedTags.join(", ")}`}
          </div>
          {showFavoritesOnly && favoritesCount > 0 && (
            <Button
              onClick={() => {
                clearFavorites();
                setShowFavoritesOnly(false);
              }}
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1"
            >
              <X size={14} />
              Clear all
            </Button>
          )}
        </div>
      )}

      {/* Styles Grid with Favorites Panel */}
      <div className="space-y-4">
        {!hideStyleHeading && <h2 className="text-2xl font-heading font-semibold">Choose your style</h2>}

        {showFavoritesOnly && favoritesCount > 1 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <GripVertical size={12} />
            Drag to reorder favorites
          </p>
        )}

        <div className={`flex gap-6 ${showFavoritesOnly ? "" : ""}`}>
          {/* Main Styles Grid */}
          <div className={`${showFavoritesOnly && favorites.length > 0 ? "flex-1" : "w-full"}`}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={showFavoritesOnly ? favorites : []}
                strategy={viewMode === "grid" ? rectSortingStrategy : verticalListSortingStrategy}
              >
                <div
                  className={
                    viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "flex flex-col gap-3"
                  }
                  role="list"
                  aria-label="Text styles"
                >
                  {styles.map((style) => {
                    const displayText = inputText.trim() || "Text.onl";
                    const transformedText = transformText(processText(displayText), style.unicodeMap);
                    const isFocused = focusedStyle === style.slug;
                    // Truncate preview text if too long
                    const truncatedText =
                      transformedText.length > truncateLength
                        ? transformedText.slice(0, truncateLength) + "..."
                        : transformedText;

                    // List View Layout
                    if (viewMode === "list") {
                      const listCard = (
                        <Card
                          className={`p-3 transition-all cursor-pointer hover:shadow-card ${
                            isFocused ? "ring-2 ring-primary shadow-glow" : ""
                          } ${showFavoritesOnly ? "touch-none" : ""}`}
                          onClick={() => setFocusedStyle(style.slug)}
                          role="listitem"
                          tabIndex={0}
                          aria-label={`${style.name}: ${style.shortDescription}`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setFocusedStyle(style.slug);
                              handleCopy(transformedText, style.name);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            {showFavoritesOnly && (
                              <div className="text-muted-foreground cursor-grab active:cursor-grabbing">
                                <GripVertical size={16} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3
                                  className="font-semibold text-sm hover:text-primary hover:underline cursor-pointer transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/style/${style.slug}`;
                                  }}
                                >
                                  {style.name}
                                </h3>
                                {showDescriptions && (
                                  <span className="text-xs text-muted-foreground hidden sm:inline">
                                    {style.shortDescription}
                                  </span>
                                )}
                              </div>
                              <p className="font-mono text-sm truncate mt-1" title={transformedText}>
                                {truncatedText || style.example}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(transformedText, style.name);
                                }}
                                size="sm"
                                className="gap-1"
                              >
                                <Copy size={14} />
                                Copy
                              </Button>
                              {currentStyleSlug !== style.slug && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/style/${style.slug}`;
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="gap-1 hidden sm:flex"
                                  aria-label={`Try ${style.name} style`}
                                >
                                  <Play size={14} />
                                  Try
                                </Button>
                              )}
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(style);
                                }}
                                variant="outline"
                                size="sm"
                                className="hidden sm:flex"
                                aria-label={`Share ${style.name} style`}
                              >
                                <Share2 size={14} />
                              </Button>
                              <div onClick={(e) => e.stopPropagation()} className="hidden sm:block">
                                <ImageExport text={transformedText} styleName={style.name} />
                              </div>

                              {/* Mobile actions dropdown - modal={false} prevents body scroll lock conflict with ImageExport dialog */}
                              <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 sm:hidden"
                                    aria-label={`More actions for ${style.name}`}
                                  >
                                    <MoreVertical size={14} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  {currentStyleSlug !== style.slug && (
                                    <DropdownMenuItem onClick={() => (window.location.href = `/style/${style.slug}`)}>
                                      <Play size={14} className="mr-2" />
                                      Try
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleShare(style)}>
                                    <Share2 size={14} className="mr-2" />
                                    Share
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                    <ImageExport text={transformedText} styleName={style.name} asMenuItem />
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>

                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(style.slug);
                                }}
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={isFavorite(style.slug) ? "Remove from favorites" : "Add to favorites"}
                              >
                                <Heart
                                  size={16}
                                  className={
                                    isFavorite(style.slug) ? "fill-red-500 text-red-500" : "text-muted-foreground"
                                  }
                                />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );

                      if (showFavoritesOnly) {
                        return (
                          <SortableItem key={style.slug} id={style.slug}>
                            {listCard}
                          </SortableItem>
                        );
                      }
                      return <div key={style.slug}>{listCard}</div>;
                    }

                    // Grid View Layout (default)
                    const gridCard = (
                      <Card
                        className={`p-4 transition-all cursor-pointer hover:shadow-card ${
                          isFocused ? "ring-2 ring-primary shadow-glow" : ""
                        } ${showFavoritesOnly ? "touch-none" : ""}`}
                        onClick={() => setFocusedStyle(style.slug)}
                        role="listitem"
                        tabIndex={0}
                        aria-label={`${style.name}: ${style.shortDescription}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setFocusedStyle(style.slug);
                            handleCopy(transformedText, style.name);
                          } else if (e.key === " " && e.shiftKey) {
                            e.preventDefault();
                            onStyleSelect?.(style.slug);
                          }
                        }}
                      >
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              {showFavoritesOnly && (
                                <div className="text-muted-foreground cursor-grab active:cursor-grabbing mt-0.5">
                                  <GripVertical size={16} />
                                </div>
                              )}
                              <div>
                                <h3
                                  className="font-semibold text-sm hover:text-primary hover:underline cursor-pointer transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/style/${style.slug}`;
                                  }}
                                  title={`View ${style.name} style details`}
                                >
                                  {style.name}
                                </h3>
                                {showDescriptions && (
                                  <p className="text-xs text-muted-foreground">{style.shortDescription}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(style.slug);
                                }}
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 shrink-0"
                                aria-label={isFavorite(style.slug) ? "Remove from favorites" : "Add to favorites"}
                              >
                                <Heart
                                  size={16}
                                  className={
                                    isFavorite(style.slug)
                                      ? "fill-red-500 text-red-500"
                                      : "text-muted-foreground hover:text-red-500"
                                  }
                                />
                              </Button>
                            </div>
                          </div>

                          <div className="bg-muted/30 rounded-lg p-3">
                            <p className="text-lg font-mono break-all leading-relaxed" title={transformedText}>
                              {truncatedText || style.example}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(transformedText, style.name);
                              }}
                              size="sm"
                              className="gap-2"
                              aria-label={`Copy ${style.name} text`}
                            >
                              <Copy size={14} />
                              Copy
                            </Button>

                            {currentStyleSlug !== style.slug && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/style/${style.slug}`;
                                }}
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                aria-label={`Try ${style.name} style`}
                              >
                                <Play size={14} />
                                Try
                              </Button>
                            )}

                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(style);
                              }}
                              variant="outline"
                              size="sm"
                              aria-label={`Share ${style.name} style`}
                            >
                              <Share2 size={14} />
                            </Button>

                            <div onClick={(e) => e.stopPropagation()} className="hidden lg:block">
                              <ImageExport text={transformedText} styleName={style.name} />
                            </div>

                            {/* Mobile/Tablet actions dropdown - modal={false} prevents body scroll lock conflict with ImageExport dialog */}
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 lg:hidden"
                                  aria-label={`More actions for ${style.name}`}
                                >
                                  <MoreVertical size={14} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                {/* Export Image - show on mobile and tablet (hidden on lg+) */}
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <ImageExport text={transformedText} styleName={style.name} asMenuItem />
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {style.tags.slice(0, 5).map((tag) => (
                              <button
                                key={tag}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // If hideFilters is true (platform pages) or on style detail page, navigate to /styles with tag
                                  if (hideFilters || window.location.pathname.startsWith("/style/")) {
                                    navigate(`/styles?tag=${encodeURIComponent(tag)}`);
                                  } else {
                                    // We're on /styles or home page - just update the filter
                                    if (!selectedTags.includes(tag)) {
                                      setSelectedTags((prev) => [...prev, tag]);
                                    }
                                  }
                                }}
                                className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full hover:bg-accent/80 transition-colors cursor-pointer"
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      </Card>
                    );

                    if (showFavoritesOnly) {
                      return (
                        <SortableItem key={style.slug} id={style.slug}>
                          {gridCard}
                        </SortableItem>
                      );
                    }
                    return <div key={style.slug}>{gridCard}</div>;
                  })}
                </div>
              </SortableContext>
            </DndContext>

            {/* Load More Button */}
            {hasMoreStyles && !hideLoadMore && (
              <div className="flex justify-center mt-8">
                <Button onClick={loadMoreStyles} variant="outline" size="lg" className="min-w-[200px]">
                  Load More ({visibleCount} of {totalFilteredCount})
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!hideStyleHeading && (
        <div className="text-center text-sm text-muted-foreground flex items-center justify-center gap-4">
          <p>
            Press <kbd className="px-1.5 py-0.5 text-xs bg-muted rounded border">?</kbd> for keyboard shortcuts
          </p>
          <KeyboardShortcutsButton onClick={() => setShowShortcuts(true)} />
        </div>
      )}

      <KeyboardShortcuts open={showShortcuts} onOpenChange={setShowShortcuts} />

      <SurpriseMeModal
        isOpen={showSurpriseModal}
        onClose={() => setShowSurpriseModal(false)}
        inputText={surpriseText}
        onCopy={handleSurpriseCopy}
      />
    </div>
  );
}